import React, { createContext, useCallback, useContext, ReactNode } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │  REVOLUT SDK ISOLATION BOUNDARY                                         │
 * │                                                                        │
 * │  This file is the ONE place in the app that touches                    │
 * │  `@revolut/checkout` (RevolutCheckout.js). The exact widget API and    │
 * │  token semantics could not be verified against the live docs, so if    │
 * │  the real SDK behaves differently, FIX IT HERE — nowhere else.         │
 * │                                                                        │
 * │  Callers use `useRevolut().mountCardField(...)` and receive a small    │
 * │  controller ({ submit, destroy }); they never import the SDK or touch  │
 * │  its types directly.                                                    │
 * │                                                                        │
 * │  Flow: POST /billing/subscribe returns a `revolut_token`. We load the  │
 * │  SDK, create an embedded card field bound to that token, and let the   │
 * │  caller submit it. On the SDK's `onSuccess` the caller confirms via    │
 * │  POST /billing/subscription/confirm; the webhook is the authoritative  │
 * │  backstop. PCI: raw card data is entered ONLY inside the Revolut       │
 * │  iframe and never reaches our backend — we send the public order       │
 * │  token, never card fields.                                             │
 * └───────────────────────────────────────────────────────────────────────┘
 */

// Public key + environment come from Vite env. The embedded card-field flow is
// driven by the per-order `revolut_token`; the public key is read here so the
// single boundary owns all Revolut config (and is the knob to wire in if the
// live SDK requires it, e.g. `RevolutCheckout.payments({ publicToken })`).
const REVOLUT_PUBLIC_KEY = import.meta.env.VITE_REVOLUT_PUBLIC_KEY as string | undefined;
const REVOLUT_MODE = ((import.meta.env.VITE_REVOLUT_ENV as string) || 'sandbox') as
  | 'sandbox'
  | 'prod'
  | 'dev'
  | 'stub';

// Non-prod test mode: render a fake card field (success/decline buttons) instead
// of loading the real SDK, so the full flow is testable without a Revolut
// account. Mirrors the backend stub (docs/api/providers/revolut-testing.md).
// Never active when the mode is explicitly 'prod'.
const STUB_MODE = REVOLUT_MODE === 'stub' || (REVOLUT_MODE !== 'prod' && !REVOLUT_PUBLIC_KEY);

/** Optional cardholder details forwarded to the widget on submit. */
export interface CardholderMeta {
  name?: string;
  email?: string;
}

/** Handle returned to the caller for the mounted card field. */
export interface RevolutFieldController {
  /** Submit the entered card details to process the payment. */
  submit: (meta?: CardholderMeta) => void;
  /** Tear down the card-field iframe. Safe to call more than once. */
  destroy: () => void;
}

export interface MountCardFieldParams {
  /** `revolut_token` returned by POST /billing/subscribe. */
  token: string;
  /** An empty element the card-field iframe mounts into. */
  target: HTMLElement;
  /** Fired when the payment completes successfully. */
  onSuccess: () => void;
  /** Fired with a user-friendly message when the payment fails. */
  onError: (message: string) => void;
  /** Fired as the field's validity changes (true = ready to submit). */
  onValidChange?: (valid: boolean) => void;
}

interface RevolutContextValue {
  mountCardField: (params: MountCardFieldParams) => Promise<RevolutFieldController>;
}

const RevolutContext = createContext<RevolutContextValue | undefined>(undefined);

/**
 * Test-mode card field: renders success/decline buttons into `target` so the
 * subscribe → confirm flow can be driven end-to-end without the real SDK.
 */
function mountStubField(params: MountCardFieldParams): RevolutFieldController {
  const { target, onSuccess, onError, onValidChange } = params;
  onValidChange?.(true);
  target.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.setAttribute('data-testid', 'revolut-stub-field');
  wrap.className =
    'rounded-lg border border-dashed border-warning-400 bg-warning-50 dark:bg-warning-900/20 p-4 space-y-3';

  const note = document.createElement('p');
  note.className = 'text-xs text-warning-800 dark:text-warning-200';
  note.textContent = 'Test mode — no real card is charged. Choose an outcome:';

  const row = document.createElement('div');
  row.className = 'flex flex-wrap gap-2';

  const okBtn = document.createElement('button');
  okBtn.type = 'button';
  okBtn.textContent = 'Simulate successful card';
  okBtn.className =
    'px-3 py-1.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600';
  okBtn.onclick = () => onSuccess();

  const failBtn = document.createElement('button');
  failBtn.type = 'button';
  failBtn.textContent = 'Simulate declined card';
  failBtn.className =
    'px-3 py-1.5 rounded-full text-sm font-medium border border-error-400 text-error-700 dark:text-error-300 hover:bg-error-50 dark:hover:bg-error-900/20';
  failBtn.onclick = () => onError('Your card was declined (test mode).');

  row.appendChild(okBtn);
  row.appendChild(failBtn);
  wrap.appendChild(note);
  wrap.appendChild(row);
  target.appendChild(wrap);

  return {
    submit: () => onSuccess(),
    destroy: () => {
      target.innerHTML = '';
    },
  };
}

export function RevolutProvider({ children }: { children: ReactNode }) {
  const mountCardField = useCallback(
    async (params: MountCardFieldParams): Promise<RevolutFieldController> => {
      const { token, target, onSuccess, onError, onValidChange } = params;

      // Non-prod test mode: skip the SDK entirely.
      if (STUB_MODE) {
        return mountStubField(params);
      }

      if (!REVOLUT_PUBLIC_KEY) {
        // Fail fast at the boundary so misconfiguration surfaces clearly.
        onError('Card payments are not configured (missing VITE_REVOLUT_PUBLIC_KEY).');
        throw new Error('VITE_REVOLUT_PUBLIC_KEY is not configured');
      }

      // Lazy-load so the SDK bundle only ships when checkout is actually opened.
      const { default: RevolutCheckout } = await import('@revolut/checkout');
      const instance = await RevolutCheckout(token, REVOLUT_MODE);

      const field = instance.createCardField({
        target,
        onSuccess,
        onError: (error) =>
          onError(error?.message || 'Card payment failed. Please check your details and try again.'),
        onValidation: (errors) => onValidChange?.(errors.length === 0),
      });

      return {
        submit: (meta) => field.submit(meta),
        destroy: () => {
          try {
            instance.destroy();
          } catch {
            // Best-effort teardown; ignore double-destroy / already-gone races.
          }
        },
      };
    },
    []
  );

  return <RevolutContext.Provider value={{ mountCardField }}>{children}</RevolutContext.Provider>;
}

export function useRevolut(): RevolutContextValue {
  const context = useContext(RevolutContext);
  if (context === undefined) {
    throw new Error('useRevolut must be used within a RevolutProvider');
  }
  return context;
}
